import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { selectSession } from '../../../store/authReducer';
import { IoSearchSharp, IoCalendarOutline } from 'react-icons/io5';
import { FaRegCircleCheck, FaRegClock, FaRegUser, FaRegFolderOpen } from 'react-icons/fa6';
import moment from 'moment';
import InlineEditTable from '../../../core/components/InlineEditTable/InlineEditTable';
import { Drawer } from '../../../core/components/Drawer';
import { PageFilters, FilterConfig, FilterPlacement } from '../../../core/components/PageFilters';
import {
  UserGroupPermissions,
  addPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { useOptimisticList } from '../../../core/hooks/useOptimisticList';
import { useMaximizableDrawer } from '../../../core/hooks/useMaximizableDrawer';
import { apiError } from '../../../core/toastMessages';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import { TASK_DRAWER_WIDTH } from '../../tasks/types';
import { columns as buildProjectColumns } from '../../tasks/WorkOrders/WorkOrderTable/constants';
import { PRIORITY_LABEL_TO_VALUE } from '../../tasks/WorkOrders/QuickTasks/constants';
import QuickEntryRow from '../../tasks/WorkOrders/WorkOrderTable/QuickEntryRow';
import { getTaskPrefixes } from '../../tasks/actions';
import { selectTaskPrefixes } from '../../tasks/reducer';
import {
  TaskTemplate,
  TaskOccurrence,
  TaskTemplateStatus,
  useGetTaskTemplateQuery,
  useGetTaskTemplatesQuery,
  useSetOccurrenceStatusByIdMutation,
  useUpdateTaskTemplateMutation,
  useCreateTaskTemplateMutation,
  INCOMPLETE_STATUS_FILTER,
} from '../../../core/api/rtk/taskTemplatesApi';
import WorkOrderCreateWizard from '../components/WorkOrderCreateWizard';
import { TaskDetails } from '../components/TaskDetails';
import { TaskType, Interval } from '../../tasks/models';
import { SortState } from '../../../core/components/InlineEditTable/SearchTable/types';

const UNASSIGNED_LABEL_ID = 'Unassigned';

const statusFromOccurrence = (latest?: TaskOccurrence | null): string => {
  const raw = latest?.status;
  if (!raw) return 'New';
  const lower = String(raw).toLowerCase();
  if (lower.includes('progress')) return 'In Progress';
  if (lower.includes('planning')) return 'Planning';
  if (lower.includes('review')) return 'Review';
  if (lower.includes('completed') || lower.includes('done')) return 'Completed';
  if (lower.includes('hold')) return 'On Hold';
  return 'New';
};

const INCOMPLETE_STATUS = INCOMPLETE_STATUS_FILTER;
const DEFAULT_STATUS_FILTER = ['In Progress', 'New', INCOMPLETE_STATUS];
// Every status a work order can show, in workflow order. projectStatusOptions
// lacks Planning and Review, which the API and the status column both use.
const STATUS_FILTER_OPTIONS = [
  'New',
  'Planning',
  'In Progress',
  'Review',
  'On Hold',
  'Completed',
  INCOMPLETE_STATUS,
];

const displayStatusToApi = (display: string): TaskTemplateStatus => {
  switch (display) {
    case 'New':
      return 'Pending';
    case 'On Hold':
      return 'OnHold';
    case 'In Progress':
      return 'InProgress';
    case 'Planning':
      return 'Planning';
    case 'Review':
      return 'Review';
    case 'Completed':
      return 'Completed';
    default:
      return 'Pending';
  }
};

interface WorkOrderFilterValues {
  nameQuery: string;
  memberFilter: string[];
  statusFilter: string[];
  dateQuery: string;
  createdDateQuery: string;
  prefixFilter: string[];
}

const emptyFilters = (): WorkOrderFilterValues => ({
  nameQuery: '',
  memberFilter: [],
  statusFilter: [...DEFAULT_STATUS_FILTER],
  dateQuery: '',
  createdDateQuery: '',
  prefixFilter: [],
});

const stripHtml = (value: string): string => {
  if (!value) return '';
  return String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
};

const TasksV2WorkOrders = (): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees) as any[];
  const session = useSelector(selectSession) as any;
  const timeZone = session?.timeZone || sessionStorage.getItem('timeZone') || moment.tz.guess();
  const taskPrefixes = useSelector(selectTaskPrefixes) as Array<{ type: number; message: string }>;
  const prefixById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const p of taskPrefixes) map[p.type] = p.message;
    return map;
  }, [taskPrefixes]);

  const [filters, setFilters] = useState<WorkOrderFilterValues>(emptyFilters());
  const [sortQuery, setSortQuery] = useState<SortState | null>(null);
  const [page, setPage] = useState<number>(0);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [isNewWorkOrder, setIsNewWorkOrder] = useState<boolean>(false);
  const [draftWorkOrder, setDraftWorkOrder] = useState<TaskTemplate | null>(null);
  const { maximized, maximizedOffset, canMaximize, toggleMaximized } = useMaximizableDrawer();
  const pageSize = 25;

  const queryParams = useMemo(() => {
    const params: Record<string, any> = { type: 'Project', page, pageSize };
    if (filters.nameQuery) params.search = filters.nameQuery;
    if (filters.statusFilter.length)
      params.status = filters.statusFilter.map((s) =>
        s === INCOMPLETE_STATUS ? s : displayStatusToApi(s)
      );
    if (filters.memberFilter.length)
      params.employeeId = filters.memberFilter.map((m) =>
        m === UNASSIGNED_LABEL_ID ? 0 : Number(m)
      );
    if (filters.prefixFilter.length) params.prefix = filters.prefixFilter;
    if (filters.dateQuery) {
      params.dueDateFrom = moment.tz(filters.dateQuery, timeZone).startOf('day').toISOString();
      params.dueDateUntil = moment.tz(filters.dateQuery, timeZone).endOf('day').toISOString();
    }
    if (filters.createdDateQuery) {
      params.from = moment.tz(filters.createdDateQuery, timeZone).startOf('day').toISOString();
      params.until = moment.tz(filters.createdDateQuery, timeZone).endOf('day').toISOString();
    }
    if (sortQuery?.keyName) {
      params.key = sortQuery.keyName;
      params.direction = sortQuery.direction;
    }
    return params;
  }, [filters, page, pageSize, sortQuery, timeZone]);
  const { data, isLoading, isFetching } = useGetTaskTemplatesQuery(queryParams);
  const [updateTask] = useUpdateTaskTemplateMutation();
  const [createTask] = useCreateTaskTemplateMutation();
  const [setOccurrenceStatus] = useSetOccurrenceStatusByIdMutation();
  const { data: fetchedTemplate } = useGetTaskTemplateQuery(selectedTemplate?.id as number, {
    skip: !selectedTemplate?.id,
  });

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);
  useEffect(() => {
    if (!taskPrefixes.length) dispatch(getTaskPrefixes());
  }, [dispatch, taskPrefixes.length]);
  useEffect(() => {
    setPage(0);
  }, [filters]);

  const {
    localList: localTasks,
    updateItem: updateLocalTask,
    restoreItem: restoreLocalTask,
    listRef: localTasksRef,
  } = useOptimisticList(data?.results || [], {
    compareFields: [
      'name',
      'priority',
      'employeeId',
      'start',
      'end',
      'description',
      'archivedAt',
      'latestOccurrence',
    ],
  });

  const allWorkOrders: TaskTemplate[] = useMemo(
    () => localTasks.filter((t) => !(t as any).archivedAt),
    [localTasks]
  );

  const handleAddWorkOrder = (): void => {
    setSelectedTemplate(null);
    setDraftWorkOrder(null);
    setIsNewWorkOrder(true);
  };

  const handleRowClick = (rowData: any): void => {
    const id = rowData?.id ?? rowData?._id;
    const task = allWorkOrders.find((t) => t.id === id);
    if (!task) return;
    if (task.incomplete) {
      setSelectedTemplate(null);
      setDraftWorkOrder(task);
      setIsNewWorkOrder(true);
      return;
    }
    setIsNewWorkOrder(false);
    setSelectedTemplate(task);
  };

  const handleCloseDrawer = (): void => {
    setSelectedTemplate(null);
    setDraftWorkOrder(null);
    setIsNewWorkOrder(false);
  };

  const handleSort = useCallback((keyName: string) => {
    setSortQuery((prev) => ({
      keyName,
      direction: prev?.keyName === keyName && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const dataRows = allWorkOrders.map((task) => {
    const statusText = task.incomplete
      ? INCOMPLETE_STATUS
      : statusFromOccurrence(task.latestOccurrence);
    const employee = employees.find((e: any) => String(e.id) === String(task.employeeId));
    const employeeLabel = employee ? `${employee.lastName}, ${employee.firstName}` : 'Unassigned';
    const dueDate = task.end || task.start;
    const dueMoment = dueDate ? moment(dueDate) : null;
    const createdMoment = task.createdAt ? moment(task.createdAt) : null;
    const priorityLabel = task.priority || 'Low';
    return {
      id: task.id,
      employeeId: task.employeeId == null ? '' : task.employeeId,
      name: task.name || '',
      status: statusText,
      priority: priorityLabel,
      _priority: PRIORITY_LABEL_TO_VALUE[priorityLabel] ?? -1,
      description: stripHtml(task.description || ''),
      teamMember: employeeLabel,
      dueDate: dueMoment ? dueMoment.format('MM/DD/YYYY') : '-',
      _dueDate: dueMoment ? dueMoment.valueOf() : -1,
      created: createdMoment ? createdMoment.format('MM/DD/YYYY') : '-',
      _created: createdMoment ? createdMoment.valueOf() : -1,
      prefix: task.prefixId != null ? prefixById[task.prefixId] ?? '-' : '-',
    };
  });

  const handlePriorityChange = useCallback(
    (row: Record<string, any>, nextLabel: string) => {
      const previous = localTasksRef.current.find((t) => t.id === row.id);
      if (!previous) return;
      updateLocalTask(row.id, { priority: nextLabel } as Partial<TaskTemplate>);
      updateTask({ id: row.id, priority: nextLabel })
        .unwrap()
        .catch((err) => {
          restoreLocalTask(previous);
          apiError(err);
        });
    },
    [updateTask, updateLocalTask, restoreLocalTask, localTasksRef]
  );

  const handleMemberChange = useCallback(
    (row: Record<string, any>, nextValue: string | number | '') => {
      const employeeId =
        nextValue === undefined || nextValue === null || nextValue === ''
          ? null
          : Number(nextValue);
      const previous = localTasksRef.current.find((t) => t.id === row.id);
      if (!previous) return;
      updateLocalTask(row.id, { employeeId } as Partial<TaskTemplate>);
      updateTask({ id: row.id, employeeId })
        .unwrap()
        .catch((err) => {
          restoreLocalTask(previous);
          apiError(err);
        });
    },
    [updateTask, updateLocalTask, restoreLocalTask, localTasksRef]
  );

  const handleStatusChange = useCallback(
    (row: Record<string, any>, nextLabel: string) => {
      const previous = localTasksRef.current.find((t) => t.id === row.id);
      if (!previous) return;
      const occurrenceId = previous.latestOccurrence?.id;
      if (!occurrenceId) {
        apiError({
          name: 'No occurrence',
          message:
            'Status edits become available once the scheduler creates an occurrence for this work order.',
        });
        return;
      }
      const apiStatus = displayStatusToApi(nextLabel);
      const nextLatest: TaskOccurrence = {
        ...previous.latestOccurrence!,
        status: apiStatus,
      };
      updateLocalTask(row.id, { latestOccurrence: nextLatest } as Partial<TaskTemplate>);
      setOccurrenceStatus({ occurrenceId, status: apiStatus })
        .unwrap()
        .catch((err) => {
          restoreLocalTask(previous);
          apiError(err);
        });
    },
    [setOccurrenceStatus, updateLocalTask, restoreLocalTask, localTasksRef]
  );

  const handleDueDateChange = useCallback(
    (row: Record<string, any>, date: Date | null) => {
      if (!date) return;
      const iso = moment(date).toISOString();
      const previous = localTasksRef.current.find((t) => t.id === row.id);
      if (!previous) return;
      updateLocalTask(row.id, { start: iso, end: iso } as Partial<TaskTemplate>);
      updateTask({ id: row.id, start: iso, end: iso })
        .unwrap()
        .catch((err) => {
          restoreLocalTask(previous);
          apiError(err);
        });
    },
    [updateTask, updateLocalTask, restoreLocalTask, localTasksRef]
  );

  const handleCellCommit = useCallback(
    (payload: {
      row: Record<string, any>;
      keyName: string;
      value: any;
      mode: 'change' | 'commit';
    }) => {
      const { row, keyName, value, mode } = payload;
      if (keyName !== 'name') return;
      if (mode === 'change') {
        updateLocalTask(row.id, { [keyName]: value } as Partial<TaskTemplate>);
        return;
      }
      if (mode === 'commit') {
        const previous = localTasksRef.current.find((t) => t.id === row.id);
        if (!previous) return;
        if ((previous.name || '') === value) return;
        updateTask({ id: row.id, name: value })
          .unwrap()
          .catch((err) => {
            restoreLocalTask(previous);
            apiError(err);
          });
      }
    },
    [updateTask, updateLocalTask, restoreLocalTask, localTasksRef]
  );

  const handleCreateWorkOrder = useCallback(
    (values: Record<string, any>): boolean => {
      const name = String(values?.name || '').trim();
      if (!name) return false;
      const description = values?.description ? String(values.description).trim() : null;
      const due =
        values?.dueDate instanceof Date
          ? moment(values.dueDate).toISOString()
          : moment().toISOString();
      const employeeId =
        values?.teamMember === undefined || values?.teamMember === null || values?.teamMember === ''
          ? null
          : Number(values.teamMember);
      const prefixId = values?.prefix ? Number(values.prefix) : undefined;
      createTask({
        name,
        description,
        start: due,
        end: due,
        prefixId,
        priority: values?.priority || 'Medium',
        employeeId,
        type: TaskType.Project as unknown as string,
        interval: Interval.Once as unknown as string,
      })
        .unwrap()
        .catch((err) => apiError(err));
      return true;
    },
    [createTask]
  );

  const columnOptions = useMemo(
    () => ({
      intl,
      employees,
      prefixes: taskPrefixes,
      onMemberChange: handleMemberChange,
      onPriorityChange: handlePriorityChange,
      onStatusChange: handleStatusChange,
      onDueDateChange: handleDueDateChange,
    }),
    [
      intl,
      employees,
      taskPrefixes,
      handleMemberChange,
      handlePriorityChange,
      handleStatusChange,
      handleDueDateChange,
    ]
  );

  const tableColumns = useMemo(() => buildProjectColumns(columnOptions), [columnOptions]);

  const paginationInfo = {
    total: data?.total || 0,
    page: data?.page ?? page,
    pageSize: data?.pageSize ?? pageSize,
    goToPage: (next: number) => setPage(next),
  };

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: 'status',
        type: 'select',
        name: intl.formatMessage({ id: 'filterByStatus', defaultMessage: 'Status' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaRegCircleCheck />,
        options: STATUS_FILTER_OPTIONS.map((s: string) => ({
          value: s,
          text: s,
          selected: DEFAULT_STATUS_FILTER.includes(s),
        })),
        onChange: (values: string[]) => setFilters((prev) => ({ ...prev, statusFilter: values })),
      },
      {
        id: 'dueDate',
        type: 'date',
        name: intl.formatMessage({ id: 'filterByDueDate', defaultMessage: 'Due Date' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaRegClock />,
        onChange: (date: Date | null) =>
          setFilters((prev) => ({ ...prev, dateQuery: date ? date.toISOString() : '' })),
      },
      {
        id: 'name',
        type: 'search',
        name: intl.formatMessage({ id: 'name', defaultMessage: 'Name' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <IoSearchSharp />,
        onChange: (value: string | null) =>
          setFilters((prev) => ({ ...prev, nameQuery: value ?? '' })),
      },
      {
        id: 'assignee',
        type: 'select',
        name: intl.formatMessage({ id: 'filterByTeamMember', defaultMessage: 'Assignee' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <FaRegUser />,
        options: [
          { value: UNASSIGNED_LABEL_ID, text: 'Unassigned' },
          ...(employees || []).map((e: any) => ({
            value: String(e.id),
            text: `${e.firstName} ${e.lastName}`,
          })),
        ],
        onChange: (values: string[]) => setFilters((prev) => ({ ...prev, memberFilter: values })),
      },
      {
        id: 'createdAt',
        type: 'date',
        name: intl.formatMessage({ id: 'filterByCreatedOn', defaultMessage: 'Created' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <IoCalendarOutline />,
        onChange: (date: Date | null) =>
          setFilters((prev) => ({ ...prev, createdDateQuery: date ? date.toISOString() : '' })),
      },
      {
        id: 'prefix',
        type: 'select',
        name: intl.formatMessage({ id: 'category', defaultMessage: 'Category' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <FaRegFolderOpen />,
        options: taskPrefixes.map((p) => ({ value: String(p.type), text: p.message })),
        onChange: (values: string[]) => setFilters((prev) => ({ ...prev, prefixFilter: values })),
      },
    ],
    [intl, employees, taskPrefixes]
  );

  const isDrawerOpen = isNewWorkOrder || selectedTemplate !== null;

  return (
    <>
      <div className="filters-actions-container mb-3">
        <PageFilters filters={filterConfigs} maxChicletFilters={4} />
        <div className="page-actions">
          <UserGroupPermissions itemRef={addPermissionRefs.task}>
            <button className="button primary" onClick={handleAddWorkOrder} type="button">
              {intl.formatMessage({ id: 'addWorkOrder' })}
            </button>
          </UserGroupPermissions>
        </div>
      </div>

      <div className="tasks-table">
        <InlineEditTable
          api=""
          columns={tableColumns}
          rows={dataRows}
          paginationInfo={paginationInfo}
          isLoading={isLoading || isFetching}
          rowClickInfo={{ callback: handleRowClick }}
          onCellCommit={handleCellCommit}
          sortState={sortQuery ?? undefined}
          onSort={handleSort}
          entryRow={{
            onSubmit: handleCreateWorkOrder,
            render: (ctx) => <QuickEntryRow options={columnOptions} {...ctx} />,
          }}
        />
      </div>

      <Drawer
        expanded={isDrawerOpen}
        maximized={isNewWorkOrder && maximized}
        maximizedOffset={maximizedOffset}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        onBackdropClick={handleCloseDrawer}
      >
        {isDrawerOpen &&
          (isNewWorkOrder ? (
            <WorkOrderCreateWizard
              draft={draftWorkOrder}
              maximized={maximized}
              onToggleMaximize={canMaximize ? toggleMaximized : undefined}
              onClose={handleCloseDrawer}
            />
          ) : (
            <TaskDetails
              template={fetchedTemplate ?? null}
              onClose={handleCloseDrawer}
              onSave={handleCloseDrawer}
            />
          ))}
      </Drawer>
    </>
  );
};

export default TasksV2WorkOrders;
